import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import "leaflet/dist/leaflet.css";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TablePagination,
  Tooltip,
  TextField,
  Typography,
} from "@mui/material";
import { createIncident } from '../services/offlineApi';
import { loadVaultHistory, visibleVaultHistory } from '../api/vaultHistory';
import { loadVaultRequirements } from '../api/vaultRequirements';
import { createDispatchRefresh } from '../api/dispatchRefresh';
import UploadRecoveryPanel from '../components/UploadRecoveryPanel';
import BatchVideoUpload from '../components/BatchVideoUpload';
import { prepareUploads, deliverUpload, attachOriginalFile } from '../services/uploadQueue';
import { readSession, requireSession, sessionIsCurrent, operationId } from '../services/submissionSession';
import { deliverIncident } from '../services/submissionQueue';
import DashboardLayout from "../layouts/DashboardLayout";
import { AuthContext } from "../context/AuthContext";
import api, { TOKEN_STORAGE_KEY } from "../api/client";
import {
  INCIDENT_TYPES,
  SERVICE_OPTIONS,
  SOURCE_OPTIONS,
} from "../constants/incidentOptions";
import trainingModules from "../constants/trainingModules";

// Build a URL with authentication token for video/image elements
const buildSecuredUrl = (rawUrl) => {
  if (!rawUrl) return "";
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) return rawUrl;
  const separator = rawUrl.includes("?") ? "&" : "?";
  return `${rawUrl}${separator}access_token=${encodeURIComponent(token)}`;
};

// Format incident type for display: "assist-motorist" -> "ASSIST MOTORIST"
const formatIncidentType = (type) => {
  if (!type) return "Unknown";
  return type.replace(/-/g, ' ').toUpperCase();
};

const normalizeIncidentTypeValue = (rawType) => {
  if (typeof rawType !== "string") return null;
  const normalized = rawType
    .trim()
    .toUpperCase()
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ");

  if (!normalized) {
    return null;
  }

  if (INCIDENT_TYPES.includes(normalized)) {
    return normalized;
  }

  if (normalized.includes("DEBRIS")) return "DEBRIS";
  if (normalized.includes("ASSIST OTHER AGENCY")) return "ASSIST OTHER AGENCY";
  if (normalized.includes("ASSIST MOTORIST")) return "ASSIST MOTORIST";
  if (normalized.includes("ACCIDENT")) return "ACCIDENT";
  if (normalized.includes("TEST")) return "TEST";

  return null;
};

const resolveIncidentTypeValue = (primaryType, fallbackType) => (
  normalizeIncidentTypeValue(primaryType)
  || normalizeIncidentTypeValue(fallbackType)
  || INCIDENT_TYPES[0]
);




const STAT_CARDS = [
  { label: "My Incidents", key: "incidentCount" },
  { label: "My Service Hours", key: "serviceHours" },
  { label: "My Training Hours", key: "trainingHours" },
  { label: "My Expenses", key: "expenses" },
];

const CAMERA_OPTIONS = [
  { value: "dash", label: "Dash Camera" },
  { value: "body", label: "Body Camera" },
  { value: "unspecified", label: "Unspecified" },
];

const ACTIONS = [
  { label: "New Incident", action: "incident" },
  { label: "Attach Video", action: "attach" },
];

const INCIDENT_STATUS = {
  OPEN: "open",
  CLEARED: "cleared",
  INCOMPLETE: "incomplete",
};

const REQUIRED_FIELD_LABELS = {
  Notes: "Notes Needed",
  "Primary Ranger": "Primary Ranger Needed",
  "Clear Time": "Clear Time Needed",
};

const OPTIONAL_FIELD_LABELS = {
  "Other Rangers": "Other Rangers (Optional)",
};

// Dispositions that require time confirmation if under threshold
const REPAIR_DISPOSITIONS = ["MINOR REPAIR", "MAJOR REPAIR"];
const MINOR_REPAIR_THRESHOLD_MINUTES = 5;
const MAJOR_REPAIR_THRESHOLD_MINUTES = 30;
const IMPLAUSIBLE_REPAIR_DURATION_MINUTES = 2;

// Get the duration threshold for a repair disposition
const getRepairThreshold = (disposition) => {
  if (!disposition) return null;
  const upper = disposition.toUpperCase();
  if (upper.includes("MINOR REPAIR")) return MINOR_REPAIR_THRESHOLD_MINUTES;
  if (upper.includes("MAJOR REPAIR")) return MAJOR_REPAIR_THRESHOLD_MINUTES;
  return null;
};

// Check if incident has a repair disposition - returns the disposition name or null
const findRepairDisposition = (servicesProvided) => {
  if (!Array.isArray(servicesProvided)) return null;
  for (const service of servicesProvided) {
    const upperService = (service || "").toUpperCase();
    for (const repair of REPAIR_DISPOSITIONS) {
      if (upperService.includes(repair)) {
        return service; // Return the original service name
      }
    }
  }
  return null;
};

const normalizeServicesProvided = (servicesProvided) => {
  if (!Array.isArray(servicesProvided)) return [];
  return servicesProvided
    .map((service) => (typeof service === "string" ? service.trim().toUpperCase() : ""))
    .filter((service) => service.length > 0);
};

const isSafetyCheckOnlyDisposition = (servicesProvided) => {
  const normalized = normalizeServicesProvided(servicesProvided);
  return normalized.length === 1 && normalized[0] === "SAFETY CHECK";
};

// Calculate duration in minutes from start and end times
const calculateDurationMinutes = (occurredAtUtc, occurredEndUtc) => {
  if (!occurredAtUtc || !occurredEndUtc) return null;
  const start = new Date(occurredAtUtc);
  const end = new Date(occurredEndUtc);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  return (end.getTime() - start.getTime()) / 60000;
};

const getRepairTimeReview = ({ occurredAtUtc, occurredEndUtc, incidentType, servicesProvided, durationConfirmedAtUtc }) => {
  const empty = {
    needsTimeConfirmation: false,
    needsTimeCorrection: false,
    durationMinutes: null,
    repairDisposition: null,
  };

  if (!occurredAtUtc || !occurredEndUtc || durationConfirmedAtUtc) {
    return empty;
  }

  const duration = calculateDurationMinutes(occurredAtUtc, occurredEndUtc);
  if (duration === null) {
    return empty;
  }

  const roundedDuration = Math.max(0, Math.round(duration));
  const isDebrisIncident = normalizeIncidentTypeValue(incidentType) === "DEBRIS";
  const safetyCheckOnly = isSafetyCheckOnlyDisposition(servicesProvided);

  if (duration <= IMPLAUSIBLE_REPAIR_DURATION_MINUTES) {
    if (!isDebrisIncident && !safetyCheckOnly) {
      return {
        needsTimeConfirmation: false,
        needsTimeCorrection: true,
        durationMinutes: roundedDuration,
        repairDisposition: null,
      };
    }

    return empty;
  }

  const foundDisposition = findRepairDisposition(servicesProvided);
  const threshold = getRepairThreshold(foundDisposition);

  if (threshold === null || duration >= threshold) {
    return empty;
  }

  return {
    needsTimeConfirmation: true,
    needsTimeCorrection: false,
    durationMinutes: roundedDuration,
    repairDisposition: foundDisposition,
  };
};

const getMissingIncidentRequirements = (incidentSummary, detail, videoSettings) => {
  const missingRequiredFields = [];
  const missingOptionalFields = [];

  if (!detail) {
    const summaryYear = incidentSummary?.occurredAtUtc
      ? new Date(incidentSummary.occurredAtUtc).getFullYear()
      : 0;
    // Check exemption from video settings
    const typeUpper = (incidentSummary?.incidentType || "").toUpperCase().trim();
    const sourceUpper = (incidentSummary?.source || "").toUpperCase().trim();
    const isExempt = videoSettings && (
      !videoSettings.videoRequiredByDefault
      || (videoSettings.exemptIncidentTypes || []).includes(typeUpper)
      || (videoSettings.exemptSources || []).includes(sourceUpper)
    );
    return {
      missingRequiredFields: ["Details Unavailable"],
      missingOptionalFields: [],
      needsVideo: !isExempt && summaryYear >= 2026 && (incidentSummary?.videoCount ?? 0) === 0,
      videoBypassReason: null,
    };
  }

  if (!detail.notes || !detail.notes.trim()) {
    missingRequiredFields.push("Notes");
  }

  if (!detail.primaryRangerId || !detail.primaryRangerId.trim()) {
    missingRequiredFields.push("Primary Ranger");
  }

  if (!detail.occurredEndUtc) {
    missingRequiredFields.push("Clear Time");
  }

  const bypassReasonRaw = typeof detail.videoBypassReason === "string" ? detail.videoBypassReason : "";
  const bypassReasonTrimmed = bypassReasonRaw.trim();
  const bypassReason = bypassReasonTrimmed.length > 0 ? bypassReasonTrimmed : null;

  const pendingBypassRaw = typeof detail.pendingVideoBypassReason === "string"
    ? detail.pendingVideoBypassReason
    : "";
  const pendingBypassTrimmed = pendingBypassRaw.trim();
  const pendingBypassReason = pendingBypassTrimmed.length > 0 ? pendingBypassTrimmed : null;
  const hasPendingVideoBypass = Boolean(detail?.hasPendingVideoBypass) || Boolean(pendingBypassReason);

  // Only require video for incidents in 2026 or later, respecting admin exemptions
  const incidentYear = detail.occurredAtUtc ? new Date(detail.occurredAtUtc).getFullYear() : 0;
  // Use the freshest video count: prefer detail.videos (individual fetch) over summary.videoCount (list fetch)
  const videoCount = detail?.videos?.length ?? incidentSummary?.videoCount ?? 0;
  const typeUpper = (detail.incidentType || "").toUpperCase().trim();
  const sourceUpper = (detail.source || "").toUpperCase().trim();
  const isExemptFromVideo = videoSettings && (
    !videoSettings.videoRequiredByDefault
    || (videoSettings.exemptIncidentTypes || []).includes(typeUpper)
    || (videoSettings.exemptSources || []).includes(sourceUpper)
  );
  const needsVideo = !isExemptFromVideo && incidentYear >= 2026 && videoCount === 0 && !bypassReason;

  const repairTimeReview = getRepairTimeReview({
    occurredAtUtc: detail.occurredAtUtc,
    occurredEndUtc: detail.occurredEndUtc,
    incidentType: detail.incidentType,
    servicesProvided: detail.servicesProvided,
    durationConfirmedAtUtc: detail.durationConfirmedAtUtc,
  });

  return {
    missingRequiredFields,
    missingOptionalFields,
    needsVideo,
    videoBypassReason: bypassReason,
    hasPendingVideoBypass,
    pendingVideoBypassReason: pendingBypassReason,
    needsTimeConfirmation: repairTimeReview.needsTimeConfirmation,
    needsTimeCorrection: repairTimeReview.needsTimeCorrection,
    durationMinutes: repairTimeReview.durationMinutes,
    repairDisposition: repairTimeReview.repairDisposition,
  };
};

const toInputDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const toIsoString = (value) => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const numberOrUndefined = (value) => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

function MyVault() {
  const { user } = useContext(AuthContext);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const buildIncidentForm = useCallback(
    () => ({
      source: "",
      incidentType: "",
      notes: "",
      cadId: "",
      occurredAt: "",
      occurredEnd: "",
      locationDescription: "",
      latitude: "",
      longitude: "",
      servicesProvided: [],
      relatedIncidentId: "",
      vehicleYear: "",
      vehicleMake: "",
      vehicleModel: "",
      vehicleColor: "",
      vehicleState: "",
      vehiclePlate: "",
    }),
    []
  );

  const [incidentHistory, setIncidentHistory] = useState({ ownerUserId: null, rangerId: null, items: [] });
  const incidents = useMemo(() => visibleVaultHistory(incidentHistory, user), [incidentHistory, user]);
  const setIncidents = useCallback(items => {
    const session = readSession();
    if (session?.ownerUserId !== Number(user?.id)) return;
    setIncidentHistory({ ownerUserId: session.ownerUserId, rangerId: session.profile.rangerId?.trim().toUpperCase(), items });
  }, [user?.id]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [incidentsError, setIncidentsError] = useState("");

  const [incidentOpen, setIncidentOpen] = useState(false);
  const pendingCallRef = useRef(null);
  const incidentOwnerRef = useRef(null);
  const incidentSaveFlightRef = useRef(false);
  const [incidentForm, setIncidentForm] = useState(() => buildIncidentForm());
  const [incidentMode, setIncidentMode] = useState("create");
  const [clips, setClips] = useState([]);
  const [incidentSubmitting, setIncidentSubmitting] = useState(false);
  const [incidentAlert, setIncidentAlert] = useState(null);
  
  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState({
    isUploading: false,
    isProcessing: false,
    currentFile: 0,
    totalFiles: 0,
    fileName: "",
    bytesUploaded: 0,
    totalBytes: 0,
    speedMBps: 0,
    percentage: 0,
  });
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editIncidentId, setEditIncidentId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editAlert, setEditAlert] = useState(null);
  const [editTimeConfirmation, setEditTimeConfirmation] = useState(null);
  const [deletePrompt, setDeletePrompt] = useState({ open: false, incident: null });
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const requirementsOwner = `${user?.id || ""}:${user?.rangerId?.trim().toUpperCase() || ""}`;
  const [historyViewState, setHistoryViewState] = useState({ owner: null, query: "", page: 0, pageSize: 10 });
  const historyView = historyViewState.owner === requirementsOwner
    ? historyViewState : { query: "", page: 0, pageSize: 10 };
  const updateHistoryView = update => setHistoryViewState(previous => ({
    ...(previous.owner === requirementsOwner ? previous : { query: "", page: 0, pageSize: 10 }),
    ...update, owner: requirementsOwner,
  }));
  const matchingHistory = useMemo(() => {
    const query = historyView.query.trim().toLowerCase();
    if (!query) return incidents;
    return incidents.filter(incident => [incident.cadId, incident.incidentType, formatIncidentType(incident.incidentType),
      incident.notes, incident.vehicle?.year, incident.vehicle?.make, incident.vehicle?.model,
      incident.vehicle?.color, incident.vehicle?.plate, incident.vehicle?.state,
      ...(Array.isArray(incident.servicesProvided) ? incident.servicesProvided : []),
    ].filter(value => value != null).join(" ").toLowerCase().includes(query));
  }, [incidents, historyView.query]);
  const historyPage = Math.min(historyView.page, Math.max(0, Math.ceil(matchingHistory.length / historyView.pageSize) - 1));
  const visibleHistory = useMemo(() => matchingHistory.slice(historyPage * historyView.pageSize, (historyPage + 1) * historyView.pageSize),
    [matchingHistory, historyPage, historyView.pageSize]);
  const [missingInfoState, setMissingInfoState] = useState({ owner: null, items: {} });
  const requirementsViewRef = useRef(null);
  const [followUpPageState, setFollowUpPageState] = useState({ owner: null, page: 0 });
  const [followUpPageSize, setFollowUpPageSize] = useState(10);
  const [requirementsRetry, setRequirementsRetry] = useState(0);
  const [missingInfoLoading, setMissingInfoLoading] = useState(false);
  const [videoBypassPrompt, setVideoBypassPrompt] = useState({
    open: false,
    incident: null,
    reason: "",
    error: "",
    submitting: false,
  });
  const [attachedVideos, setAttachedVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [expensesError, setExpensesError] = useState("");

  // Training certifications state
  const [trainingCerts, setTrainingCerts] = useState([]);

  const stats = useMemo(() => {
    const serviceMinutes = incidents.reduce((total, incident) => {
      if (!incident.occurredAtUtc || !incident.occurredEndUtc) {
        return total;
      }

      const start = new Date(incident.occurredAtUtc);
      const end = new Date(incident.occurredEndUtc);
      const diffMinutes = (end.getTime() - start.getTime()) / 60000;

      if (!Number.isFinite(diffMinutes) || diffMinutes <= 0) {
        return total;
      }

      return total + diffMinutes;
    }, 0);

    const serviceHoursValue = serviceMinutes > 0 ? serviceMinutes / 60 : 0;
    const serviceHoursDisplay = serviceHoursValue > 0 ? serviceHoursValue.toFixed(1) : "0";

    // Calculate total expenses
    const expenseTotalCents = expenses.reduce((total, expense) => total + (expense.amountCents || 0), 0);
    const expenseTotalDollars = expenseTotalCents / 100;
    const expenseDisplay = expenses.length > 0 ? `$${expenseTotalDollars.toFixed(2)}` : "$0.00";

    // Calculate training hours from approved certifications
    const approvedCourseIds = new Set(
      trainingCerts.filter((c) => c.status === "approved").map((c) => c.courseId)
    );
    const totalCreditHours = trainingModules.reduce((sum, mod) => {
      if (approvedCourseIds.has(mod.id) && mod.creditHours) {
        return sum + mod.creditHours;
      }
      return sum;
    }, 0);
    const trainingHoursDisplay = totalCreditHours > 0 ? totalCreditHours.toString() : "0";

    return {
      incidentCount: incidents.length,
      serviceHours: serviceHoursDisplay,
      trainingHours: trainingHoursDisplay,
      expenses: expenseDisplay,
    };
  }, [incidents, expenses, trainingCerts]);



  const incompleteIncidents = useMemo(
    () =>
      incidents.filter(
        (incident) => (incident.status || "").toLowerCase() === INCIDENT_STATUS.INCOMPLETE
      ),
    [incidents]
  );

  // Closed incidents with repair durations that need review
  const closedIncidentsNeedingTimeConfirmation = useMemo(() => {
    const filtered = incidents.filter((incident) => {
      const status = (incident.status || "").toLowerCase();
      if (status !== "closed" && status !== "complete") {
        return false;
      }

      const review = getRepairTimeReview({
        occurredAtUtc: incident.occurredAtUtc,
        occurredEndUtc: incident.occurredEndUtc,
        incidentType: incident.incidentType,
        servicesProvided: incident.servicesProvided,
        durationConfirmedAtUtc: incident.durationConfirmedAtUtc,
      });

      return review.needsTimeConfirmation || review.needsTimeCorrection;
    });
    return filtered;
  }, [incidents]);

  // Every incomplete/time-review call remains available; detail results only describe rows.
  const incidentsNeedingFollowUp = useMemo(() => [...incompleteIncidents, ...closedIncidentsNeedingTimeConfirmation],
    [incompleteIncidents, closedIncidentsNeedingTimeConfirmation]);
  const followUpPage = followUpPageState.owner === requirementsOwner
    ? Math.min(followUpPageState.page, Math.max(0, Math.ceil(incidentsNeedingFollowUp.length / followUpPageSize) - 1)) : 0;
  const visibleFollowUp = useMemo(() => incidentsNeedingFollowUp.slice(followUpPage * followUpPageSize, (followUpPage + 1) * followUpPageSize),
    [incidentsNeedingFollowUp, followUpPage, followUpPageSize]);
  requirementsViewRef.current = visibleFollowUp;
  const missingInfoMap = missingInfoState.owner === requirementsOwner && missingInfoState.view === visibleFollowUp ? missingInfoState.items : {};
  const setMissingInfoMap = useCallback(update => {
    if (readSession()?.ownerUserId !== Number(user?.id) || requirementsViewRef.current !== visibleFollowUp) return;
    setMissingInfoState(previous => ({ owner: requirementsOwner, view: visibleFollowUp, items: typeof update === "function"
      ? update(previous.owner === requirementsOwner && previous.view === visibleFollowUp ? previous.items : {}) : update }));
  }, [requirementsOwner, user?.id, visibleFollowUp]);

  useEffect(() => {
    const session = readSession();
    const controller = new AbortController(); let ignore = false;
    setMissingInfoMap({});
    if (!session || session.expired || session.ownerUserId !== Number(user?.id) || !user?.rangerId || visibleFollowUp.length === 0) {
      setMissingInfoLoading(false); return () => controller.abort();
    }
    setMissingInfoLoading(true);
    loadVaultRequirements({ incidents: visibleFollowUp, session, currentSession: readSession, api, signal: controller.signal })
      .then(({ results, settings }) => {
        if (ignore || !sessionIsCurrent(session)) return;
        const next = {};
        for (const { incident, detail, error } of results) next[incident.id] = error ? { loadError: error } : getMissingIncidentRequirements(incident, detail, settings);
        setMissingInfoMap(next);
      })
      .catch(error => {
        if (ignore || !sessionIsCurrent(session) || error.code === "ERR_CANCELED") return;
        setMissingInfoMap(Object.fromEntries(visibleFollowUp.map(incident => [incident.id, { loadError: "Details could not be confirmed. Open the incident or retry." }])));
      })
      .finally(() => { if (!ignore) setMissingInfoLoading(false); });
    return () => { ignore = true; controller.abort(); };
  }, [user?.id, user?.rangerId, visibleFollowUp, requirementsRetry, setMissingInfoMap]);

  const buildEditForm = useCallback((detail, fallbackIncidentType = null) => {
    const primaryId = detail?.primaryRangerId ?? "";
    const resolvedIncidentType = resolveIncidentTypeValue(detail?.incidentType, fallbackIncidentType);
    const participants = Array.isArray(detail?.participants)
      ? detail.participants
          .map((p) => p?.rangerId)
          .filter((id) => typeof id === "string" && id.trim().length > 0)
          .filter((id) => id.toUpperCase() !== primaryId.toUpperCase())
      : [];

    return {
      cadId: detail?.cadId ?? "",
      incidentType: resolvedIncidentType,
      source: detail?.source ?? "",
      occurredAt: toInputDate(detail?.occurredAtUtc),
      occurredEnd: toInputDate(detail?.occurredEndUtc),
      locationDescription: detail?.locationDescription ?? "",
      latitude:
        typeof detail?.latitude === "number" ? String(detail.latitude) : "",
      longitude:
        typeof detail?.longitude === "number" ? String(detail.longitude) : "",
      primaryRangerId: detail?.primaryRangerId ?? "",
      notes: detail?.notes ?? "",
      participantInput: participants.join(", "),
      participantRangerIds: participants,
      vehicleYear: detail?.vehicle?.year ?? "",
      vehicleMake: detail?.vehicle?.make ?? "",
      vehicleModel: detail?.vehicle?.model ?? "",
      vehicleColor: detail?.vehicle?.color ?? "",
      vehicleState: detail?.vehicle?.state ?? "",
      vehiclePlate: detail?.vehicle?.plate ?? "",
      servicesProvided: Array.isArray(detail?.servicesProvided)
        ? detail.servicesProvided
        : [],
    };
  }, []);

  const openIncidentEditor = useCallback(
    async (incidentRef) => {
      const incidentId = typeof incidentRef === "object" ? incidentRef?.id : incidentRef;
      const fallbackIncidentType = typeof incidentRef === "object"
        ? incidentRef?.incidentType
        : null;

      if (!incidentId) {
        setEditAlert({ type: "error", message: "Unable to open incident editor." });
        return;
      }

      setEditDialogOpen(true);
      setEditIncidentId(incidentId);
      setEditForm(null);
      setEditAlert(null);
      setEditLoading(true);
      setAttachedVideos([]);
      setSelectedVideo(null);
      setEditTimeConfirmation(null);

      try {
        const response = await api.get(`/incidents/${incidentId}`);
        const detail = response.data;
        const resolvedIncidentType = resolveIncidentTypeValue(detail?.incidentType, fallbackIncidentType);
        setEditForm(buildEditForm(detail, fallbackIncidentType));
        setAttachedVideos(Array.isArray(detail.videos) ? detail.videos : []);

        const review = getRepairTimeReview({
          occurredAtUtc: detail.occurredAtUtc,
          occurredEndUtc: detail.occurredEndUtc,
          incidentType: detail.incidentType,
          servicesProvided: detail.servicesProvided,
          durationConfirmedAtUtc: detail.durationConfirmedAtUtc,
        });

        if (review.needsTimeConfirmation || review.needsTimeCorrection) {
          setEditTimeConfirmation({
            needed: true,
            requiresCorrection: review.needsTimeCorrection,
            durationMinutes: review.durationMinutes,
            repairDisposition: review.repairDisposition,
            incidentType: resolvedIncidentType,
            originalOccurredAt: toInputDate(detail.occurredAtUtc),
            originalOccurredEnd: toInputDate(detail.occurredEndUtc),
          });
        }
      } catch (err) {
        console.error(err);
        setEditAlert({
          type: "error",
          message: "Unable to load incident details right now.",
        });
      } finally {
        setEditLoading(false);
      }
    },
    [buildEditForm]
  );

  const handleEditClose = () => {
    if (!editSaving) {
      setEditDialogOpen(false);
      setAttachedVideos([]);
      setSelectedVideo(null);
      setEditTimeConfirmation(null);
    }
  };

  const handleEditChange = (field) => (event) => {
    const value = event.target.value;
    setEditForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleParticipantInputChange = (event) => {
    const value = event.target.value;
    const ids = value
      .split(",")
      .map((token) => token.trim())
      .filter((token) => token.length > 0);

    setEditForm((prev) =>
      prev
        ? {
            ...prev,
            participantInput: value,
            participantRangerIds: ids,
          }
        : prev
    );
  };

  const handleEditServicesChange = (event) => {
    const {
      target: { value },
    } = event;

    setEditForm((prev) =>
      prev
        ? {
            ...prev,
            servicesProvided: typeof value === "string" ? value.split(",") : value,
          }
        : prev
    );
  };

  const handleEditSubmit = async (event, options = {}) => {
    event.preventDefault();

    const { confirmDuration = false } = options;

    if (!editIncidentId || !editForm) {
      return;
    }

    if (editTimeConfirmation?.requiresCorrection && confirmDuration) {
      setEditAlert({
        type: "error",
        message: "This incident requires time correction. Update Start/End times, then save changes.",
      });
      return;
    }

    if (editTimeConfirmation?.requiresCorrection) {
      const timesUnchanged =
        editForm.occurredAt === editTimeConfirmation.originalOccurredAt
        && editForm.occurredEnd === editTimeConfirmation.originalOccurredEnd;

      if (timesUnchanged) {
        setEditAlert({
          type: "error",
          message: "Please update Start and/or End time before saving. This duration needs correction.",
        });
        return;
      }
    }

    setEditSaving(true);
    setEditAlert(null);

    const normalizeVehicleField = (value) => {
      if (typeof value !== "string") {
        return null;
      }

      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    };

    const payload = {
      cadId: editForm.cadId?.trim() || undefined,
      incidentType: editForm.incidentType,
      occurredAtUtc: toIsoString(editForm.occurredAt),
      occurredEndUtc: toIsoString(editForm.occurredEnd),
      latitude: numberOrUndefined(editForm.latitude),
      longitude: numberOrUndefined(editForm.longitude),
      locationDescription: editForm.locationDescription ?? "",
      primaryRangerId: editForm.primaryRangerId ?? "",
      source: editForm.source?.trim() || undefined,
      notes: editForm.notes ?? "",
      participantRangerIds: (editForm.participantRangerIds ?? []).filter(
        (id) => !editForm.primaryRangerId || id.toUpperCase() !== editForm.primaryRangerId.toUpperCase()
      ),
      servicesProvided: Array.isArray(editForm.servicesProvided)
        ? editForm.servicesProvided
        : [],
      vehicle: {
        year: normalizeVehicleField(editForm.vehicleYear),
        make: normalizeVehicleField(editForm.vehicleMake),
        model: normalizeVehicleField(editForm.vehicleModel),
        color: normalizeVehicleField(editForm.vehicleColor),
        state: normalizeVehicleField(editForm.vehicleState),
        plate: normalizeVehicleField(editForm.vehiclePlate),
      },
      confirmDuration,
    };

    try {
      await api.put(`/incidents/${editIncidentId}`, payload);
      setIncidentAlert({ type: "success", message: "Incident updated successfully." });
      setEditDialogOpen(false);
      setEditTimeConfirmation(null);
      fetchIncidents();
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to update this incident right now.";
      setEditAlert({
        type: "error",
        message:
          typeof message === "string"
            ? message
            : "Unable to update this incident right now.",
      });
    } finally {
      setEditSaving(false);
    }
  };

  // Each mounted account scope owns its reader. StrictMode setup gets a new
  // coalescer so an aborted rehearsal cannot swallow the replacement refresh.
  const historyScope = useMemo(() => ({ refresh: null }), [user?.id, user?.rangerId]);
  const fetchIncidents = useCallback(async () => {
    const session = readSession(), refresh = historyScope.refresh;
    if (!refresh || !session || session.ownerUserId !== Number(user?.id) || !session.profile.rangerId) return;
    setIncidentsLoading(true); setIncidentsError('');
    try { await refresh(); }
    catch (error) {
      if (historyScope.refresh === refresh && sessionIsCurrent(session) && error.code !== 'ERR_CANCELED')
        setIncidentsError(error.message || 'Unable to load incidents right now.');
    }
    finally { if (historyScope.refresh === refresh && sessionIsCurrent(session)) setIncidentsLoading(false); }
  }, [user?.id, historyScope]);

  useEffect(() => {
    const controller = new AbortController();
    historyScope.refresh = createDispatchRefresh({
      currentSession: () => { const session = readSession(); return !controller.signal.aborted && !session?.expired ? session?.token : null; },
      load: () => {
        const session = readSession();
        if (session?.ownerUserId !== Number(user?.id)) throw new Error('Sign in again to load your call history.');
        return loadVaultHistory({ session, currentSession: readSession, nonce: operationId(), signal: controller.signal });
      },
      apply: items => { if (!controller.signal.aborted) setIncidents(items); },
    });
    setIncidents([]); setIncidentsError(''); setIncidentsLoading(false);
    if (user?.rangerId) fetchIncidents();
    return () => { historyScope.refresh = null; controller.abort(); };
  }, [user?.id, user?.rangerId, historyScope, fetchIncidents, setIncidents]);

  const fetchExpenses = useCallback(async () => {
    setExpensesLoading(true);
    setExpensesError("");
    try {
      // Skip auto-logout on 401 to prevent logging out when expenses endpoint fails
      const response = await api.get("/expenses/my", { skipAutoLogout: true });
      setExpenses(response.data || []);
    } catch (err) {
      console.error("Error fetching expenses:", err);
      // Don't show error if it's just auth - the user can still use other features
      if (err.response?.status === 401) {
        setExpensesError("Expense tracking is temporarily unavailable.");
      } else {
        setExpensesError("Unable to load expenses right now.");
      }
    } finally {
      setExpensesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // Fetch training certifications
  useEffect(() => {
    const fetchTrainingCerts = async () => {
      try {
        const res = await api.get("/training-certifications/my");
        setTrainingCerts(res.data || []);
      } catch (err) {
        console.error("Failed to load training certifications:", err);
      }
    };
    fetchTrainingCerts();
  }, []);

  const openVideoBypassDialog = (incident) => {
    const existing = missingInfoMap[incident.id];
    const initialReason = existing?.pendingVideoBypassReason || "";
    setVideoBypassPrompt({
      open: true,
      incident,
      reason: initialReason,
      error: "",
      submitting: false,
    });
  };

  const handleMarkComplete = async (incidentId) => {
    try {
      await api.put(`/incidents/${incidentId}`, { status: "closed" });
      setIncidentAlert({ type: "success", message: "Incident marked as complete." });
      fetchIncidents();
    } catch (err) {
      console.error("Failed to mark incident complete", err);
      setIncidentAlert({ type: "error", message: "Failed to mark incident complete." });
    }
  };

  const closeVideoBypassDialog = () => {
    setVideoBypassPrompt((prev) =>
      prev.submitting
        ? prev
        : {
            open: false,
            incident: null,
            reason: "",
            error: "",
            submitting: false,
          }
    );
  };

  const handleVideoBypassReasonChange = (event) => {
    const value = event.target.value;
    setVideoBypassPrompt((prev) => ({ ...prev, reason: value }));
  };

  const handleVideoBypassSubmit = async () => {
    const { incident } = videoBypassPrompt;
    if (!incident || videoBypassPrompt.submitting) {
      return;
    }

    const trimmed = videoBypassPrompt.reason.trim();
    if (trimmed.length === 0) {
      setVideoBypassPrompt((prev) => ({
        ...prev,
        error: "Provide a short explanation before bypassing this video requirement.",
      }));
      return;
    }

    setVideoBypassPrompt((prev) => ({ ...prev, submitting: true, error: "" }));

    try {
      await api.post(`/incidents/${incident.id}/video-bypass`, {
        reason: trimmed,
      });

      setMissingInfoMap((prev) => {
        const next = { ...prev };
        const incidentId = incident.id;
        const existing = next[incidentId] ?? {};
        next[incidentId] = {
          ...existing,
          needsVideo: true,
          videoBypassReason: null,
          hasPendingVideoBypass: true,
          pendingVideoBypassReason: trimmed,
        };
        return next;
      });

      setIncidentAlert({
        type: "success",
        message: `Bypass request submitted for CAD ${incident.cadId}. Supervisor approval pending.`,
      });

      setVideoBypassPrompt({
        open: false,
        incident: null,
        reason: "",
        error: "",
        submitting: false,
      });
    } catch (err) {
      console.error(err);
      let message = err.response?.data ?? "Unable to bypass the video requirement right now.";

      if (err.response?.status === 409) {
        message = "A bypass request is already pending review for this incident.";
      }

      setVideoBypassPrompt((prev) => ({
        ...prev,
        error:
          typeof message === "string"
            ? message
            : "Unable to bypass the video requirement right now.",
        submitting: false,
      }));
    }
  };

  const handleActionClick = (action) => {
    if (action === "incident") {
      pendingCallRef.current = null;
      setIncidentMode("create");
      setIncidentForm(buildIncidentForm());
      setClips([]);
      setIncidentAlert(null);
      incidentOwnerRef.current = readSession()?.ownerUserId;
      setUploadProgress(progress => ({ ...progress, isUploading: false, isProcessing: false }));
      setIncidentOpen(true);
      return;
    }

    if (action === "attach" && incidents.length > 0) {
      const latest = incidents[0];
      setIncidentMode("attach");
      setIncidentForm(() => ({
        ...buildIncidentForm(),
        incidentType: latest.incidentType,
        relatedIncidentId: String(latest.id),
      }));
      setClips([]);
      setIncidentAlert(null);
      incidentOwnerRef.current = readSession()?.ownerUserId;
      setUploadProgress(progress => ({ ...progress, isUploading: false, isProcessing: false }));
      setIncidentOpen(true);
    }
  };

  const handleIncidentClose = () => {
    if (!incidentSubmitting) {
      setIncidentOpen(false);
    }
  };

  const handleIncidentChange = (field) => (event) => {
    const value = event.target.value;
    setIncidentForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "relatedIncidentId") {
        const selected = incidents.find((incident) => String(incident.id) === String(value));
        if (selected) {
          next.incidentType = selected.incidentType;
        }
      }
      return next;
    });
  };

  const handleServicesChange = (event) => {
    const {
      target: { value },
    } = event;
    setIncidentForm((prev) => ({
      ...prev,
      servicesProvided: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleClipFilesChange = (event) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) {
      return;
    }

    const ownerUserId = readSession()?.ownerUserId;
    if (!ownerUserId || ownerUserId !== incidentOwnerRef.current) {
      setIncidentAlert({ type: 'error', message: 'Sign in as the owner before selecting recordings.' });
      return;
    }
    setClips(prev => [...prev, ...files.map(file => ({ id: operationId(), ownerUserId, file,
      cameraType: 'unspecified', fallbackTimestamp: new Date().toISOString() }))]);

    event.target.value = "";
  };

  const handleClipCameraChange = (clipId) => (event) => {
    const value = event.target.value;
    setClips((prev) =>
      prev.map((clip) => (clip.id === clipId ? { ...clip, cameraType: value } : clip))
    );
  };

  const handleRemoveClip = (clipId) => {
    setClips((prev) => prev.filter((clip) => clip.id !== clipId));
  };

  const clipUploadInputs = incidentId => clips.map(clip => ({ ownerUserId: clip.ownerUserId, operationId: clip.id,
    file: clip.file, incidentId, cameraType: clip.cameraType, incidentType: incidentForm.incidentType,
    timestamp: incidentForm.occurredAt ? new Date(incidentForm.occurredAt).toISOString() : clip.fallbackTimestamp,
    notes: incidentForm.notes || '' }));

  const resolveIncidentId = async (isAssist, session) => {
    if (incidentMode === "attach") {
      return incidentForm.relatedIncidentId
        ? Number(incidentForm.relatedIncidentId)
        : null;
    }

    if (isAssist && !incidentForm.relatedIncidentId) {
      setIncidentAlert({
        type: "error",
        message: "Select the incident this Assist Ranger clip belongs to.",
      });
      return null;
    }

    if (isAssist && incidentForm.relatedIncidentId) {
      return Number(incidentForm.relatedIncidentId);
    }

    const vehicleProvided = [
      incidentForm.vehicleYear,
      incidentForm.vehicleMake,
      incidentForm.vehicleModel,
      incidentForm.vehicleColor,
      incidentForm.vehicleState,
      incidentForm.vehiclePlate,
    ].some(Boolean);

    const payload = {
      cadId: incidentForm.cadId?.trim() || undefined,
      incidentType: incidentForm.incidentType,
      occurredAtUtc: incidentForm.occurredAt
        ? new Date(incidentForm.occurredAt).toISOString()
        : new Date().toISOString(),
      occurredEndUtc: incidentForm.occurredEnd
        ? new Date(incidentForm.occurredEnd).toISOString()
        : undefined,
      latitude: incidentForm.latitude ? Number(incidentForm.latitude) : undefined,
      longitude: incidentForm.longitude ? Number(incidentForm.longitude) : undefined,
      locationDescription: incidentForm.locationDescription?.trim() || undefined,
      primaryRangerId: user?.rangerId ?? undefined,
      notes: incidentForm.notes?.trim() || undefined,
      participantRangerIds: [],
      servicesProvided:
        incidentForm.servicesProvided.length > 0
          ? incidentForm.servicesProvided
          : undefined,
    };

    if (vehicleProvided) {
      payload.vehicle = {
        year: incidentForm.vehicleYear || undefined,
        make: incidentForm.vehicleMake || undefined,
        model: incidentForm.vehicleModel || undefined,
        color: incidentForm.vehicleColor || undefined,
        state: incidentForm.vehicleState || undefined,
        plate: incidentForm.vehiclePlate || undefined,
      };
    }

    if (pendingCallRef.current !== null) {
      const prior = await deliverIncident(pendingCallRef.current, session.token);
      if (!prior.accepted) throw new Error(prior.message || 'Call is saved locally. Confirm the original call before uploading footage.');
      return prior.incident.id;
    }
    const saved = await createIncident({ ...payload, source: incidentForm.source }, session.token, { uploads: clipUploadInputs(null) });
    pendingCallRef.current = saved.localId;
    for (let index = 0; index < (saved.uploadLocalIds || []).length; index++)
      await attachOriginalFile(saved.uploadLocalIds[index], clips[index].file, session.token);
    if (saved.pending) throw new Error(saved.message + ' Footage has not been uploaded. Reconnect and retry this saved call.');
    return saved.incident.id;
  };

  const uploadClips = async (incidentId, session) => {
    const rows = await prepareUploads(clipUploadInputs(incidentId), session.token);
    // Every selected recording is committed locally before transmitting the first file.
    try {
      for (let index = 0; index < rows.length; index++) {
        if (!sessionIsCurrent(session)) throw new Error('Account changed. Upload details remain saved for their original owner.');
        const row = rows[index], start = Date.now();
        setUploadProgress({ isUploading: true, isProcessing: false, currentFile: index + 1, totalFiles: rows.length,
          fileName: row.file.name, bytesUploaded: 0, totalBytes: row.file.size, speedMBps: 0, percentage: 0 });
        const result = await deliverUpload(row.localId, session.token, { sendFile: true, onProgress: progress => {
          const percentage = progress.total ? Math.min(100, Math.round(progress.loaded / progress.total * 100)) : 0;
          setUploadProgress(prev => ({ ...prev, isUploading: percentage < 100, isProcessing: percentage >= 100,
            bytesUploaded: progress.loaded, totalBytes: progress.total || row.file.size, percentage,
            speedMBps: progress.loaded / Math.max(1, (Date.now() - start) / 1000) / 1048576 }));
        } });
        if (!sessionIsCurrent(session)) throw new Error('Account changed. Check Upload recovery after signing in as the owner.');
        if (!result.accepted) { const pending = new Error(result.message || 'Recording confirmation is pending. Check Upload recovery before sending it again.'); pending.uploadPending = true; throw pending; }
      }
    } finally {
      if (sessionIsCurrent(session)) setUploadProgress({ isUploading: false, isProcessing: false, currentFile: 0, totalFiles: 0,
        fileName: '', bytesUploaded: 0, totalBytes: 0, speedMBps: 0, percentage: 0 });
    }
  };

  const isAttachMode = incidentMode === "attach";

  const handleIncidentSubmit = async (event) => {
    event.preventDefault();
    if (incidentSaveFlightRef.current) return;

    // In attach mode, clips are required
    if (isAttachMode && clips.length === 0) {
      setIncidentAlert({ type: "error", message: "Attach at least one clip." });
      return;
    }

    if (!user?.rangerId) {
      setIncidentAlert({
        type: "error",
        message: "Add a Ranger ID to upload incidents.",
      });
      return;
    }

    const isAssist = incidentForm.incidentType === "Assist Ranger";

    if (!isAttachMode) {
      if (!incidentForm.source) {
        setIncidentAlert({ type: "error", message: "Select a source for this incident." });
        return;
      }

      if (!incidentForm.incidentType) {
        setIncidentAlert({ type: "error", message: "Choose an incident type." });
        return;
      }

      if (!incidentForm.occurredAt) {
        setIncidentAlert({ type: "error", message: "Provide the start date and time." });
        return;
      }
    }

    incidentSaveFlightRef.current = true;
    setIncidentSubmitting(true);
    setIncidentAlert(null);
    let submissionSession;

    try {
      const session = requireSession(localStorage.getItem(TOKEN_STORAGE_KEY));
      submissionSession = session;
      if (session.ownerUserId !== incidentOwnerRef.current || session.ownerUserId !== Number(user?.id))
        throw new Error('This form belongs to another account. Sign in as its owner to continue.');
      const incidentId = await resolveIncidentId(isAssist, session);
      if (!incidentId) {
        setIncidentSubmitting(false);
        return;
      }

      // Only upload clips if there are any
      if (clips.length > 0) {
        await uploadClips(incidentId, session);
      }

      if (!sessionIsCurrent(session)) return;
      setIncidentAlert({ type: "success", message: "Incident and selected recordings confirmed." });
      setIncidentOpen(false);
      setIncidentForm(buildIncidentForm());
      setClips([]);
      fetchIncidents();
    } catch (err) {
      if (submissionSession && !sessionIsCurrent(submissionSession)) return;
      console.error(err);
      const message = err.response?.data ?? err.message ?? "Unable to save this incident right now.";
      setIncidentAlert({
        type: err.uploadPending ? "warning" : "error",
        message:
          typeof message === "string"
            ? message
            : "Unable to save this incident right now.",
      });
    } finally {
      incidentSaveFlightRef.current = false;
      setIncidentSubmitting(false);
    }
  };

  const openDeleteIncidentDialog = (incident) => {
    if (!incident) {
      return;
    }
    setDeletePrompt({ open: true, incident });
  };

  const handleDeleteDialogClose = () => {
    if (!deleteSubmitting) {
      setDeletePrompt({ open: false, incident: null });
    }
  };

  const handleDeleteIncident = async () => {
    if (!deletePrompt.incident?.id) {
      return;
    }

    setDeleteSubmitting(true);
    try {
      await api.delete(`/incidents/${deletePrompt.incident.id}`);
      setIncidentAlert({ type: "success", message: "Call archived. Its records and footage associations are retained." });

      if (editIncidentId === deletePrompt.incident.id) {
        setEditDialogOpen(false);
      }

      fetchIncidents();
    } catch (error) {
      console.error(error);
      const message = error.response?.data ?? "Unable to archive this call right now.";
      setIncidentAlert({
        type: "error",
        message:
          typeof message === "string"
            ? message
            : "Unable to archive this call right now.",
      });
    } finally {
      setDeleteSubmitting(false);
      setDeletePrompt({ open: false, incident: null });
    }
  };

  const deleteTarget = deletePrompt.incident;

  return (
    <DashboardLayout>
      {user?.id && <BatchVideoUpload key={`batch-${user.id}`} ownerUserId={Number(user.id)} onAssigned={fetchIncidents} />}
      {user?.id && <UploadRecoveryPanel key={user.id} ownerUserId={Number(user.id)} onAccepted={fetchIncidents} />}
      {incidentAlert && (
        <Alert severity={incidentAlert.type} sx={{ mb: 3 }}>
          {incidentAlert.message}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {STAT_CARDS.map(({ label, key }) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <Paper elevation={1} sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {stats[key] ?? "--"}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Box>
        <Typography variant="h5" gutterBottom>
          Incidents Missing Info
        </Typography>

        {!user?.rangerId && (
          <Alert severity="info">
            Add a Ranger ID to your profile to start logging incidents.
          </Alert>
        )}

        {user?.rangerId && incidentsLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {user?.rangerId && !incidentsLoading && incidentsError && (
          <Alert severity="error">{incidentsError}</Alert>
        )}

        {user?.rangerId &&
          !incidentsLoading &&
          !incidentsError &&
          incidentsNeedingFollowUp.length === 0 && (
            <Alert severity="success">
              All of your incidents have the required information.
            </Alert>
          )}

        {user?.rangerId &&
          !incidentsLoading &&
          !incidentsError &&
          incidentsNeedingFollowUp.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 2 }}>
              <TableContainer>
              <Table size="small" aria-label="Follow-up incidents" sx={{ minWidth: 580 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Incident</TableCell>
                    <TableCell>Cleared</TableCell>
                    <TableCell>Primary Unit</TableCell>
                    <TableCell>Follow-up</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleFollowUp.map((incident) => {
                    const missingInfoEntry = missingInfoMap[incident.id];
                    const missingInfo = missingInfoEntry ?? {};
                    const missingRequired = missingInfo.missingRequiredFields ?? [];
                    const missingOptional = missingInfo.missingOptionalFields ?? [];
                    const needsVideo = Boolean(missingInfo.needsVideo);
                    const videoBypassReason = missingInfo.videoBypassReason;
                    const hasPendingVideoBypass = Boolean(missingInfo.hasPendingVideoBypass);
                    const pendingVideoBypassReason = missingInfo.pendingVideoBypassReason;
                    const needsTimeConfirmation = Boolean(missingInfo.needsTimeConfirmation);
                    const needsTimeCorrection = Boolean(missingInfo.needsTimeCorrection);
                    const durationMinutes = missingInfo.durationMinutes;
                    const repairDisposition = missingInfo.repairDisposition;
                    const timeReviewLabel = repairDisposition || "Incident";
                    const hasResolved = Boolean(missingInfoEntry) && !missingInfoEntry.loadError;
                    const detailError = missingInfo.loadError || (!missingInfoLoading && !missingInfoEntry ? "Details could not be confirmed. Open the incident or retry." : null);
                    const hasApprovedBypass = Boolean(videoBypassReason);
                    const videoSatisfied = !needsVideo || hasApprovedBypass;
                    const readyToClear =
                      hasResolved &&
                      videoSatisfied &&
                      !needsTimeConfirmation &&
                      !needsTimeCorrection &&
                      missingRequired.length === 0 &&
                      missingOptional.length === 0;

                    return (
                      <TableRow
                        key={`incomplete-${incident.id}`}
                        hover
                        onClick={() => openIncidentEditor(incident)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {formatIncidentType(incident.incidentType)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            CAD {incident.cadId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {incident.occurredEndUtc
                            ? new Date(incident.occurredEndUtc).toLocaleString([], {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {incident.primaryRangerId || incident.rangerIds?.[0] || "—"}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", md: "row" },
                              gap: 1.5,
                              alignItems: { xs: "flex-start", md: "center" },
                              justifyContent: { xs: "flex-start", md: "space-between" },
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              useFlexGap
                              flexWrap="wrap"
                              sx={{ alignItems: "center" }}
                            >
                              {detailError && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="caption" color="error">{detailError}</Typography>
                                  <Button size="small" onClick={event => { event.stopPropagation(); setRequirementsRetry(value => value + 1); }}>Retry details</Button>
                                </Stack>
                              )}
                              {missingInfoLoading && !hasResolved && (
                                <Chip label="Checking..." size="small" sx={{ fontWeight: 700 }} />
                              )}

                              {needsVideo && (
                                <Chip
                                  label="Video Needed"
                                  color="error"
                                  size="small"
                                  sx={{ fontWeight: 700 }}
                                />
                              )}

                              {hasPendingVideoBypass && (
                                <Tooltip
                                  title={
                                    pendingVideoBypassReason ||
                                    "Bypass request submitted; awaiting supervisor review."
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Chip
                                    label="Bypass Requested"
                                    color="warning"
                                    size="small"
                                    sx={{ fontWeight: 700 }}
                                  />
                                </Tooltip>
                              )}

                              {videoBypassReason && (
                                <Tooltip title={videoBypassReason} arrow placement="top">
                                  <Chip
                                    label="Video Bypassed"
                                    color="info"
                                    size="small"
                                    sx={{ fontWeight: 700 }}
                                  />
                                </Tooltip>
                              )}

                              {needsTimeCorrection && (
                                <Tooltip
                                  title={`${timeReviewLabel} completed in ${durationMinutes} min — this duration is implausible. Please correct start/end times before saving.`}
                                  arrow
                                  placement="top"
                                >
                                  <Chip
                                    label="Time Correction Required"
                                    color="error"
                                    size="small"
                                    sx={{ fontWeight: 700 }}
                                  />
                                </Tooltip>
                              )}

                              {needsTimeConfirmation && (
                                <Tooltip
                                  title={`${timeReviewLabel} completed in ${durationMinutes} min — please confirm start/end times are accurate`}
                                  arrow
                                  placement="top"
                                >
                                  <Chip
                                    label="Time Confirmation"
                                    color="warning"
                                    size="small"
                                    sx={{ fontWeight: 700 }}
                                  />
                                </Tooltip>
                              )}

                              {missingRequired.map((field) => (
                                <Chip
                                  key={`${incident.id}-required-${field}`}
                                  label={REQUIRED_FIELD_LABELS[field] || `Missing ${field}`}
                                  color="error"
                                  size="small"
                                  sx={{ fontWeight: 700 }}
                                />
                              ))}

                              {missingOptional.map((field) => (
                                <Chip
                                  key={`${incident.id}-optional-${field}`}
                                  label={OPTIONAL_FIELD_LABELS[field] || `Optional: ${field}`}
                                  color="warning"
                                  size="small"
                                  sx={{ fontWeight: 700 }}
                                />
                              ))}

                              {readyToClear && (
                                <Chip
                                  label="Ready to Clear"
                                  color="success"
                                  size="small"
                                  sx={{ fontWeight: 700 }}
                                />
                              )}
                            </Stack>

                            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                              {readyToClear && (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleMarkComplete(incident.id);
                                  }}
                                >
                                  Mark Complete
                                </Button>
                              )}

                              {needsVideo && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openVideoBypassDialog(incident);
                                  }}
                                  disabled={videoBypassPrompt.submitting || hasPendingVideoBypass}
                                >
                                  Bypass Video
                                </Button>
                              )}
                            </Stack>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </TableContainer>
              <TablePagination component="div" count={incidentsNeedingFollowUp.length} page={followUpPage}
                sx={{ "& .MuiTablePagination-toolbar": { flexWrap: "wrap", justifyContent: "flex-end", rowGap: 1, py: 1 },
                  "& .MuiTablePagination-spacer": { display: "none" }, "& .MuiTablePagination-actions": { ml: 1 } }}
                rowsPerPage={followUpPageSize} rowsPerPageOptions={[10, 25, 50]}
                onPageChange={(_, page) => setFollowUpPageState({ owner: requirementsOwner, page })}
                onRowsPerPageChange={event => { setFollowUpPageSize(Number(event.target.value)); setFollowUpPageState({ owner: requirementsOwner, page: 0 }); }}
              />
            </Paper>
          )}
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h5">
            Incidents
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            {ACTIONS.map(({ label, action }) => (
              <Button
                key={label}
                variant="contained"
                onClick={() => handleActionClick(action)}
                disabled={!user?.rangerId || (action === "attach" && incidents.length === 0)}
              >
                {label}
              </Button>
            ))}
          </Stack>
        </Stack>

        {!user?.rangerId && (
          <Alert severity="info">
            Add a Ranger ID to your profile to start logging incidents.
          </Alert>
        )}

        {user?.rangerId && incidentsLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {user?.rangerId && !incidentsLoading && incidentsError && (
          <Alert severity="error">{incidentsError}</Alert>
        )}

        {user?.rangerId && !incidentsLoading && !incidentsError && incidents.length === 0 && (
          <Alert severity="info">
            No incidents yet. Upload footage or log a new incident.
          </Alert>
        )}

        {incidents.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }} sx={{ mb: 2 }}>
              <TextField type="search" size="small" fullWidth label="Search my incidents"
                value={historyView.query} inputProps={{ "aria-label": "Search my incidents", maxLength: 200 }}
                helperText="Search CAD reference, type, notes, services, or vehicle."
                onChange={event => updateHistoryView({ query: event.target.value.slice(0, 200), page: 0 })} />
              {historyView.query && <Button sx={{ minHeight: 44, whiteSpace: "nowrap" }} onClick={() => updateHistoryView({ query: "", page: 0 })}>Clear search</Button>}
            </Stack>
            <Typography role="status" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {matchingHistory.length} matching {matchingHistory.length === 1 ? "incident" : "incidents"} of {incidents.length}
            </Typography>
            {matchingHistory.length === 0 && <Alert severity="info">No incidents match your search. Clear or change your search to see other calls.</Alert>}
            <Stack component="ul" aria-label="Incident history" spacing={2} sx={{ p: 0, m: 0 }}>
            {visibleHistory.map((incident) => {
              const vehicleParts = [
                incident.vehicle?.year,
                incident.vehicle?.color,
                incident.vehicle?.make,
                incident.vehicle?.model,
              ].filter(Boolean);
              const vehicleStr = vehicleParts.length > 0 ? vehicleParts.join(" ") : null;
              const plateStr =
                incident.vehicle?.plate
                  ? `${incident.vehicle.state || ""} ${incident.vehicle.plate}`.trim()
                  : null;
              const durationStr =
                incident.durationMinutes != null
                  ? incident.durationMinutes >= 60
                    ? `${Math.floor(incident.durationMinutes / 60)}h ${Math.round(incident.durationMinutes % 60)}m`
                    : `${Math.round(incident.durationMinutes)}m`
                  : null;
              const statusColor =
                incident.status === "closed" || incident.status === "complete"
                  ? "success"
                  : incident.status === "incomplete"
                  ? "warning"
                  : "info";

              return (
              <Paper component="li" aria-label={`CAD ${incident.cadId}`} key={incident.id} variant="outlined" sx={{ p: 2, listStyle: "none", overflowWrap: "anywhere" }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Row 1: Title + Status */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="h6" sx={{ mr: 1 }}>{formatIncidentType(incident.incidentType)}</Typography>
                      <Chip label={incident.status || "open"} size="small" color={statusColor} />
                      {incident.division && (
                        <Chip label={incident.division} size="small" variant="outlined" />
                      )}
                    </Stack>

                    {/* Row 2: CAD ID, Date/Time, Cleared */}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      CAD {incident.cadId} · {" "}
                      {incident.occurredAtUtc
                        ? new Date(incident.occurredAtUtc).toLocaleString()
                        : "Unknown"}
                      {incident.occurredEndUtc && (
                        <> · Cleared {new Date(incident.occurredEndUtc).toLocaleString()}</>
                      )}
                    </Typography>

                    {/* Row 3: Source, Duration, Videos */}
                    <Typography variant="body2" color="text.secondary">
                      Source: {(incident.source?.toUpperCase() === "MANUAL" || !incident.source) ? "Self" : incident.source}
                      {durationStr && <> · On-scene: {durationStr}</>}
                      {" "}· {incident.videoCount} video{incident.videoCount === 1 ? "" : "s"}
                    </Typography>

                    {/* Row 4: Vehicle info */}
                    {(vehicleStr || plateStr) && (
                      <Typography variant="body2" color="text.secondary">
                        🚗 {vehicleStr || "Unknown vehicle"}
                        {plateStr && <> · Plate: <strong>{plateStr}</strong></>}
                      </Typography>
                    )}

                    {/* Row 4b: Location */}
                    {incident.latitude != null && incident.longitude != null && (
                      <Typography variant="body2" color="text.secondary">
                        📍 {Number(incident.latitude).toFixed(5)}, {Number(incident.longitude).toFixed(5)}{" "}
                        <a
                          href={`https://www.google.com/maps?q=${incident.latitude},${incident.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#90caf9", fontSize: "0.75rem" }}
                        >
                          Map ↗
                        </a>
                      </Typography>
                    )}

                    {/* Row 5: Services provided */}
                    {incident.servicesProvided?.length > 0 && (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, lineHeight: "24px" }}>Services:</Typography>
                        {incident.servicesProvided.map((svc) => (
                          <Chip key={svc} label={svc} size="small" variant="outlined" sx={{ textTransform: "capitalize" }} />
                        ))}
                      </Stack>
                    )}

                    {/* Row 6: Notes */}
                    {incident.notes && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          p: 1,
                          backgroundColor: "action.hover",
                          borderRadius: 1,
                          fontStyle: "italic",
                          whiteSpace: "pre-wrap",
                          maxHeight: 80,
                          overflow: "auto",
                        }}
                      >
                        📝 {incident.notes}
                      </Typography>
                    )}

                    {/* Row 7: Ranger IDs */}
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                      {incident.rangerIds?.map((id) => (
                        <Chip key={id} label={id} size="small" />
                      ))}
                    </Stack>
                  </Box>
                  <Stack direction="row" justifyContent="flex-end" alignItems="flex-start" sx={{ flexWrap: "wrap", gap: 1, "& .MuiButton-root": { minHeight: 44 } }}>
                    {isAdmin && (
                      <Button
                        color="error"
                        variant="outlined"
                        disabled={deleteSubmitting}
                        onClick={() => openDeleteIncidentDialog(incident)}
                      >
                        {deleteSubmitting ? "Archiving..." : "Archive"}
                      </Button>
                    )}
                    <Button variant="contained" onClick={() => openIncidentEditor(incident)}>
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setIncidentMode("attach");
                        setIncidentForm(() => ({
                          ...buildIncidentForm(),
                          incidentType: incident.incidentType,
                          relatedIncidentId: String(incident.id),
                        }));
                        setClips([]);
                        setIncidentAlert(null);
                        incidentOwnerRef.current = readSession()?.ownerUserId;
                        setUploadProgress(progress => ({ ...progress, isUploading: false, isProcessing: false }));
                        setIncidentOpen(true);
                      }}
                    >
                      Attach Video
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
              );
            })}
            </Stack>
            <TablePagination component="nav" aria-label="Incident history pages" count={matchingHistory.length} page={historyPage}
              rowsPerPage={historyView.pageSize} rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Calls per page:"
              getItemAriaLabel={type => `Go to ${type} history page`}
              sx={{ "& .MuiTablePagination-toolbar": { flexWrap: "wrap", justifyContent: "flex-end", rowGap: 1, py: 1 },
                "& .MuiTablePagination-spacer": { display: "none" }, "& .MuiTablePagination-actions": { ml: 1 },
                "& .MuiIconButton-root": { minWidth: 44, minHeight: 44 } }}
              onPageChange={(_, page) => updateHistoryView({ page })}
              onRowsPerPageChange={event => updateHistoryView({ pageSize: Number(event.target.value), page: 0 })} />
          </Box>
        )}
      </Box>

      {/* My Expenses Section */}
      <Divider sx={{ my: 4 }} />

      <Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h5">
            My Expenses
          </Typography>
          <Button
            variant="contained"
            color="success"
            onClick={() => window.location.href = "/my-vault/resources?tab=log-expense"}
          >
            Log New Expense
          </Button>
        </Stack>

        {expensesLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!expensesLoading && expensesError && (
          <Alert severity="error">{expensesError}</Alert>
        )}

        {!expensesLoading && !expensesError && expenses.length === 0 && (
          <Alert severity="info">
            No expenses logged yet. Use the "Log New Expense" button to track your out-of-pocket expenses.
          </Alert>
        )}

        {expenses.length > 0 && (
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Item(s)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Receipt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id} hover>
                    <TableCell>
                      {expense.purchaseDate
                        ? new Date(expense.purchaseDate).toLocaleDateString([], {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {expense.categoryName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {expense.itemDescription}
                      </Typography>
                      {expense.notes && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: "block" }}>
                          {expense.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main" }}>
                        ${(expense.amountCents / 100).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {expense.receiptUrl ? (
                        <Button
                          size="small"
                          variant="outlined"
                          href={expense.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </Button>
                      ) : (
                        <Chip label="None" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      <Dialog
        open={videoBypassPrompt.open}
        onClose={closeVideoBypassDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bypass Video Requirement</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {videoBypassPrompt.incident && (
            <Typography variant="body2" color="text.secondary">
              Explain why no video can be provided for CAD {videoBypassPrompt.incident.cadId} (
              {videoBypassPrompt.incident.incidentType || "Incident"}).
            </Typography>
          )}
          <TextField
            label="Bypass Reason"
            value={videoBypassPrompt.reason}
            onChange={handleVideoBypassReasonChange}
            fullWidth
            multiline
            minRows={3}
            placeholder="Share the context for skipping video upload."
            autoFocus
            disabled={videoBypassPrompt.submitting}
            inputProps={{ maxLength: 512 }}
          />
          {videoBypassPrompt.error && (
            <Alert severity="error">{videoBypassPrompt.error}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVideoBypassDialog} disabled={videoBypassPrompt.submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleVideoBypassSubmit}
            variant="contained"
            disabled={videoBypassPrompt.submitting}
          >
            {videoBypassPrompt.submitting ? "Saving..." : "Bypass Requirement"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={incidentOpen && Number(user?.id) === incidentOwnerRef.current}
        onClose={handleIncidentClose}
        maxWidth="md"
        fullWidth
        keepMounted
      >
        <DialogTitle>
          {incidentMode === "attach" ? "Attach Video" : "New Incident"}
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleIncidentSubmit}
            sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}
          >
            {incidentAlert && <Alert severity={incidentAlert.type}>{incidentAlert.message}</Alert>}
            {clips.length > 0 && <Typography variant="body2" color="text.secondary">
              Keep the original recordings. If saving is interrupted, use Upload recovery to check confirmation and continue with the same files.
            </Typography>}
            {isAttachMode ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Choose the incident you’re updating, add any helpful notes, then drop the
                    clips you want to attach.
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    label="Incident"
                    value={incidentForm.relatedIncidentId}
                    onChange={handleIncidentChange("relatedIncidentId")}
                    fullWidth
                    required
                    helperText="Clips will be added to this incident."
                  >
                    {incidents.map((incident) => (
                      <MenuItem key={incident.id} value={String(incident.id)}>
                        {incident.cadId} — {formatIncidentType(incident.incidentType)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    value={incidentForm.notes}
                    onChange={handleIncidentChange("notes")}
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Optional context for these clips."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="outlined" component="label">
                    {clips.length > 0
                      ? `${clips.length} clip${clips.length === 1 ? "" : "s"} selected`
                      : "Attach Videos"}
                    <input
                      type="file"
                      hidden
                      accept="video/*"
                      multiple
                      onChange={handleClipFilesChange}
                    />
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Source"
                    value={incidentForm.source}
                    onChange={handleIncidentChange("source")}
                    fullWidth
                    required
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="" disabled>
                      Select a source
                    </MenuItem>
                    {SOURCE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Incident Type"
                    value={incidentForm.incidentType}
                    onChange={handleIncidentChange("incidentType")}
                    fullWidth
                    required
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="" disabled>
                      Select an incident type
                    </MenuItem>
                    {INCIDENT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    value={incidentForm.notes}
                    onChange={handleIncidentChange("notes")}
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Summarize incident details, context, and key outcomes."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="datetime-local"
                    label="Start"
                    value={incidentForm.occurredAt}
                    onChange={handleIncidentChange("occurredAt")}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="datetime-local"
                    label="End"
                    value={incidentForm.occurredEnd}
                    onChange={handleIncidentChange("occurredEnd")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Location"
                    value={incidentForm.locationDescription}
                    onChange={handleIncidentChange("locationDescription")}
                    fullWidth
                    placeholder="Where did this incident occur?"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Latitude"
                    value={incidentForm.latitude}
                    onChange={handleIncidentChange("latitude")}
                    fullWidth
                    placeholder="Optional"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Longitude"
                    value={incidentForm.longitude}
                    onChange={handleIncidentChange("longitude")}
                    fullWidth
                    placeholder="Optional"
                  />
                </Grid>
                {incidentForm.incidentType === "Assist Ranger" && (
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Related Incident"
                      value={incidentForm.relatedIncidentId}
                      onChange={handleIncidentChange("relatedIncidentId")}
                      fullWidth
                      required
                      helperText="Select the primary incident you assisted with."
                    >
                      {incidents.map((incident) => (
                        <MenuItem key={incident.id} value={String(incident.id)}>
                          {incident.cadId} — {incident.incidentType}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Vehicle Information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Year"
                    value={incidentForm.vehicleYear}
                    onChange={handleIncidentChange("vehicleYear")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Make"
                    value={incidentForm.vehicleMake}
                    onChange={handleIncidentChange("vehicleMake")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Model"
                    value={incidentForm.vehicleModel}
                    onChange={handleIncidentChange("vehicleModel")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Color"
                    value={incidentForm.vehicleColor}
                    onChange={handleIncidentChange("vehicleColor")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="State"
                    value={incidentForm.vehicleState}
                    onChange={handleIncidentChange("vehicleState")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Plate"
                    value={incidentForm.vehiclePlate}
                    onChange={handleIncidentChange("vehiclePlate")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="services-provided-label">Services Provided</InputLabel>
                    <Select
                      labelId="services-provided-label"
                      multiple
                      value={incidentForm.servicesProvided}
                      onChange={handleServicesChange}
                      input={<OutlinedInput label="Services Provided" />}
                      renderValue={(selected) =>
                        selected.length === 0 ? "None" : selected.join(", ")
                      }
                    >
                      {SERVICE_OPTIONS.map((service) => (
                        <MenuItem key={service} value={service}>
                          {service}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button variant="outlined" component="label">
                    {clips.length > 0
                      ? `${clips.length} clip${clips.length === 1 ? "" : "s"} selected`
                      : "Attach Videos"}
                    <input
                      type="file"
                      hidden
                      accept="video/*"
                      multiple
                      onChange={handleClipFilesChange}
                    />
                  </Button>
                </Grid>
              </Grid>
            )}

            {clips.length > 0 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Clips
                </Typography>
                <Stack spacing={2}>
                  {clips.map((clip) => (
                    <Paper key={clip.id} variant="outlined" sx={{ p: 2 }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2">{clip.file.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(clip.file.size / (1024 * 1024)).toFixed(1)} MB
                          </Typography>
                        </Box>
                        <TextField
                          select
                          size="small"
                          label="Camera Type"
                          value={clip.cameraType}
                          onChange={handleClipCameraChange(clip.id)}
                        >
                          {CAMERA_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Button color="error" onClick={() => handleRemoveClip(clip.id)}>
                          Remove
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Upload Progress Indicator */}
            {(uploadProgress.isUploading || uploadProgress.isProcessing) && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {uploadProgress.isProcessing 
                      ? `Processing file ${uploadProgress.currentFile} of ${uploadProgress.totalFiles}...`
                      : `Uploading file ${uploadProgress.currentFile} of ${uploadProgress.totalFiles}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {uploadProgress.isProcessing 
                      ? "Confirming recording..."
                      : uploadProgress.speedMBps > 0 
                        ? `${uploadProgress.speedMBps.toFixed(1)} MB/s`
                        : "Starting..."}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant={uploadProgress.isProcessing ? "indeterminate" : "determinate"}
                  value={uploadProgress.percentage} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: "60%" }}>
                    {uploadProgress.fileName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {uploadProgress.isProcessing 
                      ? "Transfer finished; awaiting the server receipt..."
                      : `${(uploadProgress.bytesUploaded / (1024 * 1024)).toFixed(1)} / ${(uploadProgress.totalBytes / (1024 * 1024)).toFixed(1)} MB (${uploadProgress.percentage}%)`}
                  </Typography>
                </Box>
              </Box>
            )}

            <DialogActions sx={{ px: 0 }}>
              <Button onClick={handleIncidentClose} disabled={uploadProgress.isUploading || uploadProgress.isProcessing}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={incidentSubmitting}>
                {uploadProgress.isUploading 
                  ? `Uploading ${uploadProgress.percentage}%...`
                  : uploadProgress.isProcessing
                    ? "Confirming recording..."
                    : incidentSubmitting 
                      ? "Saving..." 
                      : "Save Incident"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
        keepMounted
      >
        <DialogTitle>Edit Incident</DialogTitle>
        <DialogContent>
          {editAlert && (
            <Alert severity={editAlert.type} sx={{ my: 2 }}>
              {editAlert.message}
            </Alert>
          )}

          {editTimeConfirmation?.needed && (
            <Alert severity={editTimeConfirmation?.requiresCorrection ? "error" : "warning"} sx={{ my: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {editTimeConfirmation?.requiresCorrection
                  ? "⚠️ TIME CORRECTION REQUIRED"
                  : "⚠️ TIME CONFIRMATION REQUIRED"}
              </Typography>
              <Typography variant="body2">
                YOU LOGGED <strong>{editTimeConfirmation.repairDisposition || editTimeConfirmation.incidentType || "THIS INCIDENT"}</strong> WITHIN{" "}
                <strong>{editTimeConfirmation.durationMinutes} MINUTES</strong>?
                <br />
                {editTimeConfirmation?.requiresCorrection
                  ? "THIS DURATION IS IMPLAUSIBLE. UPDATE START/END TIMES BEFORE SAVING."
                  : "PLEASE CONFIRM START/END TIMES ARE ACCURATE."}
              </Typography>
            </Alert>
          )}

          {editLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !editForm ? (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Unable to load this incident. Please try again.
              </Typography>
              <Button
                variant="contained"
                onClick={() => editIncidentId && openIncidentEditor(editIncidentId)}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleEditSubmit}
              sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 3 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="CAD ID"
                    value={editForm.cadId}
                    onChange={handleEditChange("cadId")}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Incident Type"
                    value={editForm.incidentType}
                    onChange={handleEditChange("incidentType")}
                    fullWidth
                    required
                  >
                    {INCIDENT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Source"
                    value={editForm.source}
                    onChange={handleEditChange("source")}
                    fullWidth
                    placeholder="manual"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Primary Ranger ID"
                    value={editForm.primaryRangerId}
                    onChange={handleEditChange("primaryRangerId")}
                    fullWidth
                    placeholder="Optional"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="datetime-local"
                    label="Start"
                    value={editForm.occurredAt}
                    onChange={handleEditChange("occurredAt")}
                    fullWidth
                    required
                    color={editTimeConfirmation?.needed ? "warning" : undefined}
                    focused={editTimeConfirmation?.needed || undefined}
                    sx={editTimeConfirmation?.needed ? {
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "warning.main", borderWidth: 2 },
                      },
                    } : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="datetime-local"
                    label="End"
                    value={editForm.occurredEnd}
                    onChange={handleEditChange("occurredEnd")}
                    fullWidth
                    color={editTimeConfirmation?.needed ? "warning" : undefined}
                    focused={editTimeConfirmation?.needed || undefined}
                    sx={editTimeConfirmation?.needed ? {
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "warning.main", borderWidth: 2 },
                      },
                    } : undefined}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Location"
                    value={editForm.locationDescription}
                    onChange={handleEditChange("locationDescription")}
                    fullWidth
                    placeholder="Where did this incident occur?"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Latitude"
                    value={editForm.latitude}
                    onChange={handleEditChange("latitude")}
                    fullWidth
                    placeholder="Optional"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Longitude"
                    value={editForm.longitude}
                    onChange={handleEditChange("longitude")}
                    fullWidth
                    placeholder="Optional"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    value={editForm.notes}
                    onChange={handleEditChange("notes")}
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Summarize incident details, context, and key outcomes."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Other Rangers"
                    value={editForm.participantInput}
                    onChange={handleParticipantInputChange}
                    fullWidth
                    helperText="Optional: list additional Ranger IDs separated by commas."
                  />
                </Grid>

                {/* Attached Videos Section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Attached Videos ({attachedVideos.length})
                  </Typography>
                  {attachedVideos.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No videos attached to this incident.
                    </Typography>
                  ) : (
                    <Stack spacing={2}>
                      {/* Video selector chips */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {attachedVideos.map((video, index) => (
                          <Chip
                            key={video.id}
                            label={`${video.cameraType || "Video"} ${index + 1}${video.rangerId ? ` (${video.rangerId})` : ""}`}
                            onClick={() => setSelectedVideo(video)}
                            color={selectedVideo?.id === video.id ? "primary" : "default"}
                            variant={selectedVideo?.id === video.id ? "filled" : "outlined"}
                          />
                        ))}
                      </Box>

                      {/* Video player */}
                      {selectedVideo && (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {selectedVideo.cameraType ? selectedVideo.cameraType.charAt(0).toUpperCase() + selectedVideo.cameraType.slice(1) : "Video"} Camera
                            {selectedVideo.timestamp && ` — ${new Date(selectedVideo.timestamp).toLocaleString()}`}
                          </Typography>
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              backgroundColor: "#000",
                              borderRadius: 1,
                              overflow: "hidden",
                            }}
                          >
                            <video
                              key={selectedVideo.id}
                              controls
                              style={{ width: "100%", maxHeight: 400, display: "block" }}
                              src={buildSecuredUrl(`/api/videos/stream?id=${selectedVideo.id}`)}
                            >
                              Your browser does not support the video tag.
                            </video>
                          </Box>
                        </Paper>
                      )}
                    </Stack>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Vehicle Information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Year"
                    value={editForm.vehicleYear}
                    onChange={handleEditChange("vehicleYear")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Make"
                    value={editForm.vehicleMake}
                    onChange={handleEditChange("vehicleMake")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Model"
                    value={editForm.vehicleModel}
                    onChange={handleEditChange("vehicleModel")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Color"
                    value={editForm.vehicleColor}
                    onChange={handleEditChange("vehicleColor")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="State"
                    value={editForm.vehicleState}
                    onChange={handleEditChange("vehicleState")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Plate"
                    value={editForm.vehiclePlate}
                    onChange={handleEditChange("vehiclePlate")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="edit-services-provided-label">Services Provided</InputLabel>
                    <Select
                      labelId="edit-services-provided-label"
                      multiple
                      value={editForm.servicesProvided}
                      onChange={handleEditServicesChange}
                      input={<OutlinedInput label="Services Provided" />}
                      renderValue={(selected) =>
                        selected.length === 0 ? "None" : selected.join(", ")
                      }
                    >
                      {SERVICE_OPTIONS.map((service) => (
                        <MenuItem key={service} value={service}>
                          {service}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <DialogActions sx={{ px: 0 }}>
                {isAdmin && (
                  <Button
                    color="error"
                    onClick={() =>
                      openDeleteIncidentDialog({
                        id: editIncidentId,
                        cadId: editForm.cadId,
                        incidentType: editForm.incidentType,
                      })
                    }
                    disabled={deleteSubmitting}
                  >
                    {deleteSubmitting ? "Archiving..." : "Archive"}
                  </Button>
                )}
                <Button onClick={handleEditClose}>Cancel</Button>
                {editTimeConfirmation?.needed && !editTimeConfirmation?.requiresCorrection && (
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={(e) => handleEditSubmit(e, { confirmDuration: true })}
                    disabled={editSaving}
                  >
                    {editSaving ? "Confirming..." : "Confirm Time & Save"}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={editSaving || (
                    Boolean(editTimeConfirmation?.requiresCorrection)
                    && editForm?.occurredAt === editTimeConfirmation?.originalOccurredAt
                    && editForm?.occurredEnd === editTimeConfirmation?.originalOccurredEnd
                  )}
                >
                  {editSaving ? "Saving..." : "Save Changes"}
                </Button>
              </DialogActions>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={deletePrompt.open}
        onClose={handleDeleteDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Archive Call</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Archiving this call retains its records, participants and footage associations. It will leave active dispatch and normal reports.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              {deleteTarget?.cadId
                ? `CAD ${deleteTarget.cadId} · ${deleteTarget.incidentType || "Unknown"}`
                : "Select a call to archive."}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteIncident}
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? "Archiving..." : "Archive Call"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default MyVault;
